import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Box, Tooltip, Typography } from "@mui/material"

import type { EntityType } from "types/common.types"

import useIsCut from "helpers/hooks/useIsCut"
import useRoutes from "helpers/hooks/useRoutes"
import { getEntityHref } from "helpers/utils/entities"
import { nameOrUnnamed } from "helpers/utils/translations"
import EntityIcon from "./EntityIcon"

/**
 * The box each mark is drawn in. An SVG fits its viewBox inside the box, so a
 * landscape mark paints short and a portrait one narrow at the same number:
 * the span mark gets a wider box and the device mark a taller one so all five
 * carry the same optical weight beside 13.5px text.
 */
const GLYPH_SIZE: Partial<Record<EntityType, number>> = { span: 17, device: 15 }
const SQUARE_GLYPH_SIZE = 13

/**
 * Every mark sits in a box this wide, whatever it paints inside it, so a column
 * of references starts its names at one x rather than wherever each mark
 * happens to end. The widest of the five.
 */
const GLYPH_BOX = 17

interface EntityLinkProps {
  entity: EntityType
  id: number
  name: string | null | undefined
  /** The second line of a two-line reference: the glyph belongs to the first,
   *  so its box stays empty and the two names start at one x. */
  noGlyph?: boolean
  /** Muted, for a reference that is context rather than the subject. */
  secondary?: boolean
  /** Cuts a long name after this many lines, with the full name in a tooltip. */
  maxLines?: number
}

/**
 * An entity named and linked the way the devices table's "Installed on" cell
 * names one: its type icon, then its name, bold and unnamed in italics. Shared
 * so every surface that points at another entity reads the same.
 */
const EntityLink = ({
  entity,
  id,
  name,
  noGlyph,
  secondary,
  maxLines,
}: EntityLinkProps) => {
  const { t } = useTranslation()
  const { baseOrgURL } = useRoutes()
  const label = nameOrUnnamed(name, t)
  // Only a name that was actually cut needs the tooltip.
  const { ref: labelRef, isCut } = useIsCut<HTMLSpanElement>(label, Boolean(maxLines))

  return (
    <Link
      to={getEntityHref(entity, id, baseOrgURL)}
      aria-label={label}
      style={{
        textDecoration: "none",
        ...(maxLines && { minWidth: 0, maxWidth: "100%" }),
      }}
    >
      {/* Inline, so it centres on the text's midline rather than sitting on
          its own baseline, which leaves the glyph riding above the sentence. */}
      <Box
        display="inline-flex"
        alignItems="center"
        gap={0.5}
        sx={{ verticalAlign: "middle", ...(maxLines && { maxWidth: "100%" }) }}
      >
        <Box
          sx={{
            width: `${GLYPH_BOX}px`,
            flexShrink: 0,
            display: "flex",
            justifyContent: "center",
          }}
        >
          {!noGlyph && (
            <EntityIcon
              entity={entity}
              size={GLYPH_SIZE[entity] ?? SQUARE_GLYPH_SIZE}
              tooltip={t(`entities.${entity.toUpperCase()}`)}
            />
          )}
        </Box>
        <Tooltip title={isCut ? label : ""}>
          <Typography
            component="span"
            // The devices table sets the rhythm: a name is body1 and bold, what
            // sits under it is body2 and muted, the same as a device over its id.
            variant={secondary ? "body2" : "body1"}
            ref={labelRef}
            sx={{
              // An italic name leans past its box, so the cut would clip its last letter.
              ...(maxLines && { minWidth: 0, ...(!name && { paddingRight: "0.15em" }) }),
              ...(maxLines === 1 && {
                display: "block",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }),
              ...(maxLines &&
                maxLines > 1 && {
                  display: "-webkit-box",
                  WebkitLineClamp: maxLines,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }),
              fontWeight: secondary ? 400 : 600,
              fontStyle: name ? "normal" : "italic",
              color: secondary ? "text.secondary" : "text.primary",
              "&:hover": { color: "primary.main" },
            }}
          >
            {label}
          </Typography>
        </Tooltip>
      </Box>
    </Link>
  )
}

export default EntityLink
